import * as React from 'react';

import { HierarchicalSelectOption } from './types';

export interface ExitingOption {
  option: HierarchicalSelectOption;
  leaving: boolean;
}

/**
 * Keeps options mounted for a short while after they leave `options`, flagging
 * them as `leaving` so the UI can play a fade-out transition before removal.
 *
 * A change to `resetKey` (e.g. switching view or search) snaps the list to the
 * new options instantly, so only "in place" removals (deselecting an item)
 * animate.
 */
export const useExitingOptions = (
  options: HierarchicalSelectOption[],
  {
    enabled,
    resetKey,
    timeout = 250,
  }: { enabled: boolean; resetKey: string; timeout?: number },
): ExitingOption[] => {
  const [rendered, setRendered] = React.useState<ExitingOption[]>(() =>
    options.map((option) => ({ option, leaving: false })),
  );
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const resetRef = React.useRef(resetKey);

  React.useEffect(() => {
    const clearAll = () => {
      timers.current.forEach((timer) => clearTimeout(timer));
      timers.current.clear();
    };

    // Snap (no animation) when disabled or when the view/search context changed
    if (!enabled || resetRef.current !== resetKey) {
      resetRef.current = resetKey;
      clearAll();
      setRendered(options.map((option) => ({ option, leaving: false })));
      return;
    }

    const targetIds = new Set(options.map((opt) => opt.id));

    setRendered((prev) => {
      const seen = new Set<string>();
      const result: ExitingOption[] = [];

      prev.forEach(({ option }) => {
        seen.add(option.id);

        if (targetIds.has(option.id)) {
          // Still present: cancel any pending removal and refresh the option
          const timer = timers.current.get(option.id);
          if (timer) {
            clearTimeout(timer);
            timers.current.delete(option.id);
          }
          const fresh = options.find(
            (opt) => opt.id === option.id,
          ) as HierarchicalSelectOption;
          result.push({ option: fresh, leaving: false });
        } else {
          // Gone: keep it mounted and fading, then remove after the transition
          result.push({ option, leaving: true });
          if (!timers.current.has(option.id)) {
            const timer = setTimeout(() => {
              timers.current.delete(option.id);
              setRendered((cur) =>
                cur.filter((entry) => entry.option.id !== option.id),
              );
            }, timeout);
            timers.current.set(option.id, timer);
          }
        }
      });

      // Append newly added options
      options.forEach((option) => {
        if (!seen.has(option.id)) {
          const timer = timers.current.get(option.id);
          if (timer) {
            clearTimeout(timer);
            timers.current.delete(option.id);
          }
          result.push({ option, leaving: false });
        }
      });

      return result;
    });
  }, [options, enabled, resetKey, timeout]);

  // Clear pending timers on unmount
  React.useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => clearTimeout(timer));
      map.clear();
    };
  }, []);

  return rendered;
};

export default useExitingOptions;
